import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ShieldCheck, LogIn, Loader2 } from "lucide-react";

export default function VerifyStart() {
  const { guildId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startOAuth = async () => {
    setLoading(true);
    setError(null);
    try {
      // guildId may be a vanity slug (Pro) or a real snowflake — the backend
      // handles both in /verify/start, so we can pass through either way.
      const r = await api.post("/verify/start", { guild_id: guildId });
      // Full-page redirect into Discord's OAuth screen
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start verification.");
      setLoading(false);
    }
  };

  // Auto-kick off the flow on landing so the user doesn't have to click twice.
  // Wait a beat so they can read the header, then redirect.
  useEffect(() => {
    const t = setTimeout(() => { startOAuth(); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white flex items-center justify-center px-6" data-testid="verify-start">
      <div className="max-w-md w-full border border-[#10B981]/30 bg-[#0D0E15] p-8 space-y-6 rounded-md">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md border border-[#10B981]/50 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#10B981]" />
          </div>
          <div>
            <div className="font-display font-black text-xl">Verify with Discord</div>
            <div className="font-mono text-[10px] tracking-widest text-[#64748B] uppercase">ModNode Verification</div>
          </div>
        </div>

        <p className="text-sm text-[#E2E8F0] leading-relaxed">
          Log in with your Discord account so we can confirm it's really you, then we'll unlock the server.
          Only your Discord username is read — nothing is posted, no messages are sent.
        </p>

        {error ? (
          <div className="border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] text-sm p-3 rounded-md font-mono">
            {error}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[#94A3B8] font-mono text-xs">
            <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
            {loading ? "Opening Discord…" : "Preparing verification…"}
          </div>
        )}

        <button
          data-testid="verify-oauth-btn"
          onClick={startOAuth}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-display font-bold text-sm rounded-md disabled:opacity-50"
        >
          <LogIn className="w-4 h-4" />
          {loading ? "Redirecting…" : "Continue with Discord"}
        </button>

        <div className="font-mono text-[10px] text-[#64748B] text-center">
          Guild ID · {guildId}
        </div>
      </div>
    </div>
  );
}
